import React, { useEffect, useState } from "react";
import { Modal, Form, Select, InputNumber, Input, Switch } from "antd";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { useNavigate } from "react-router-dom";

const INVOLVEMENT_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Occasional", label: "Occasional" },
  { value: "Non-participant", label: "Non-participant" },
];

const RATING_OPTIONS = [
  { value: "Excellent", label: "Excellent" },
  { value: "Good", label: "Good" },
  { value: "Average", label: "Average" },
  { value: "Needs Improvement", label: "Needs Improvement" },
];

const ActivityParticipationFormModal = ({
  visible,
  onCancel,
  onSave,
  loading,
  editRecord = null,
  events = [],
}) => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [involvementLevel, setInvolvementLevel] = useState("Active");
  const [hasAward, setHasAward] = useState(false);

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setInvolvementLevel("Active");
      setHasAward(false);
      return;
    }
    if (editRecord) {
      form.setFieldsValue({
        eventID: editRecord.eventID,
        studentID: editRecord.studentID,
        involvementLevel: editRecord.involvementLevel,
        performanceRating: editRecord.performanceRating,
        score: editRecord.score,
        remarks: editRecord.remarks,
        hasAward: editRecord.hasAward,
        awardTitle: editRecord.awardTitle,
      });
      setInvolvementLevel(editRecord.involvementLevel || "Active");
      setHasAward(editRecord.hasAward || false);
    }
  }, [visible, editRecord, form]);

  useEffect(() => {
    if (!visible) return;
    Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.academics.classTeacherStudents,
      null,
      navigate,
    )
      .then((res) => {
        if (res.ok)
          setStudents(
            Array.isArray(res.data) ? res.data : res.data?.results || [],
          );
      })
      .catch(() => {});
  }, [visible, navigate]);

  async function handleOk() {
    const values = await form.validateFields();
    if (values.involvementLevel === "Non-participant") {
      delete values.performanceRating;
      delete values.score;
    }
    if (!values.hasAward) {
      delete values.awardTitle;
    }
    onSave(values);
  }

  const isNonParticipant = involvementLevel === "Non-participant";

  return (
    <Modal
      title={editRecord ? "Edit Participation" : "Record Participation"}
      open={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText="Save"
      width={520}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="eventID"
          label="Event"
          rules={[{ required: true, message: "Select an event" }]}
        >
          <Select
            showSearch
            placeholder="Select event"
            optionFilterProp="label"
            options={events.map((e) => ({
              value: e.id,
              label: `${e.title} (${e.scheduledDate})`,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="studentID"
          label="Student"
          rules={[{ required: true, message: "Select a student" }]}
        >
          <Select
            showSearch
            placeholder="Search student"
            optionFilterProp="label"
            options={students.map((s) => ({
              value: s.id,
              label: [
                s.fullName ||
                  `${s.firstName || ""} ${s.middleName || ""} ${s.lastName || ""}`.trim() ||
                  String(s.id),
                [s.className, s.streamName].filter(Boolean).join(" - "),
              ]
                .filter(Boolean)
                .join(" | "),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="involvementLevel"
          label="Involvement Level"
          rules={[{ required: true }]}
        >
          <Select
            options={INVOLVEMENT_OPTIONS}
            onChange={(v) => {
              setInvolvementLevel(v);
              if (v === "Non-participant") {
                form.setFieldsValue({
                  performanceRating: undefined,
                  score: undefined,
                  hasAward: false,
                  awardTitle: undefined,
                });
                setHasAward(false);
              }
            }}
          />
        </Form.Item>
        {!isNonParticipant && (
          <>
            <Form.Item name="performanceRating" label="Performance Rating">
              <Select
                options={RATING_OPTIONS}
                allowClear
                placeholder="Select rating"
              />
            </Form.Item>
            <Form.Item name="score" label="Score (numeric, if applicable)">
              <InputNumber
                min={0}
                max={100}
                step={0.5}
                style={{ width: "100%" }}
                placeholder="e.g. 92.5"
              />
            </Form.Item>
          </>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} placeholder="Coach or teacher remarks..." />
        </Form.Item>
        {!isNonParticipant && (
          <>
            <Form.Item
              name="hasAward"
              label="Award Won?"
              valuePropName="checked"
            >
              <Switch onChange={setHasAward} />
            </Form.Item>
            {hasAward && (
              <Form.Item
                name="awardTitle"
                label="Award Title"
                rules={[{ required: true, message: "Enter award title" }]}
              >
                <Input placeholder="e.g. 1st Place, Best Performer" />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

export default ActivityParticipationFormModal;
